import { useContext, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import { func, object, string } from 'prop-types'
import uuid from 'uuid/v4'
import { CvsAutoComplete } from '@components'
import { accountContext, targetContext } from '@contexts'
import { colors } from '@global/constants'
import { api } from 'api'
import { t } from '@lingui/core/macro'

const LinkError = styled(ErrorOutlineIcon)`
  margin-left: 10px;
`

const useStyles = makeStyles(theme => ({
  error: {
    color: theme.palette.error.main,
  },
}))

export const AAValidation = ({ validation = {}, type = string, onChange = () => {} }) => {
  const baseOptions = {
    aa_pixel_exists: {
      metadata: {},
      slug: 'aa_pixel_exists',
      uuid: uuid(),
    },
    aa_correct_report_suite: {
      metadata: {},
      slug: 'aa_correct_report_suite',
      uuid: uuid(),
    },
    aa_correct_report: {
      metadata: {},
      slug: 'aa_correct_report',
      uuid: uuid(),
    },
  }

  const getRsSelection = () => {
    let selection
    if (Object.keys(validation.options).length == 0) {
      selection = 'target'
    } else if (
      validation.options.aa_correct_report_suite &&
      Object.keys(validation.options.aa_correct_report_suite.metadata).length == 0
    ) {
      selection = 'target'
    } else if (validation.options.aa_correct_report_suite?.metadata.user_provided_account_id) {
      selection = 'explore'
    } else if (validation.options.aa_correct_report_suite?.metadata.expected_report_suite_id) {
      selection = 'input'
    } else {
      selection = 'target'
    }
    return selection
  }

  const classes = useStyles()

  const { byId, fetchAccounts } = useContext(accountContext)
  const TargetContext = useContext(targetContext)
  const [rsSelection, setRsSelection] = useState(getRsSelection())
  const [realms, setRealms] = useState([])
  const [reports, setReports] = useState([])

  const vali = {
    ...validation,
    options: {
      aa_pixel_exists: {
        ...baseOptions.aa_pixel_exists,
        ...validation?.options?.aa_pixel_exists,
      },
      aa_correct_report: {
        ...baseOptions.aa_correct_report,
        ...validation?.options?.aa_correct_report,
      },
      aa_correct_report_suite: {
        ...baseOptions.aa_correct_report_suite,
        ...validation?.options?.aa_correct_report_suite,
      },
    },
  }

  // todo: the saved ones don't have the right schema? So what do we do about that?
  // keep a representation of it here?, and map the validation over the options?
  // or always take the past in validation and mutate it to have the right option types?
  // other wise the whole handle change business doesn't work so good.
  const selectedAccount = parseInt(
    vali.options.aa_correct_report_suite.metadata.user_provided_account_id,
  )
  const selectedRealm = vali.options.aa_correct_report_suite.metadata.expected_report_suite_id
  const selectedTarget = parseInt(vali.options.aa_correct_report_suite.metadata.expected_target_id)
  const selectedReport = vali.options.aa_correct_report.metadata.expected_report_id

  useEffect(() => {
    if (!Object.keys(byId).length) {
      fetchAccounts()
    }
    if (!Object.keys(TargetContext.byId).length) {
      TargetContext.fetchTargets()
    }
    if (selectedAccount) {
      fetchRealms(selectedAccount)
    }
    if (selectedRealm) {
      fetchReports(selectedRealm)
    }
  }, [])

  // set report configuration option type
  if (rsSelection) {
    // do nothing
  } else if (selectedTarget) {
    setRsSelection('target')
  } else if (selectedAccount) {
    setRsSelection('explore')
  } else if (!selectedAccount && selectedReport) {
    setRsSelection('input')
  }

  const fetchRealms = account => {
    api.post(`/api/v1/account/connector/${account}/realm/`).then(res => {
      setRealms(res.data.realms)
    })
  }

  const fetchReports = realm => {
    api.get(`/api/v1/account/connector/${selectedAccount}/realm/${realm}/target/list`).then(res => {
      setReports(res.data.targets)
    })
  }

  const getReportSuiteConfigStatus = () => {
    let status
    switch (rsSelection) {
      case 'target':
        status = !selectedTarget
        break
      case 'explore':
        status = !selectedAccount || !selectedRealm
        break
      case 'input':
        status = !vali.options.aa_correct_report_suite.metadata.expected_report_suite_id
        break
      default:
        status = false
        break
    }
    return status
  }

  const getVerifyReportStatus = () => {
    let status
    switch (rsSelection) {
      case 'target':
        status = false
        break
      case 'explore':
        status = !selectedReport
        break
      case 'input':
        status = !vali.options.aa_correct_report.metadata.expected_report_id
        break
      default:
        status = false
        break
    }
    return status
  }

  return (
    <div>
      <Grid container style={{ width: 'calc(100% - 35px)' }}>
        <Grid item xs={4}>
          <FormControlLabel
            style={{ margin: 0 }}
            control={
              <Checkbox
                checked={vali.options?.aa_pixel_exists.enabled}
                onChange={(e, newVal) => {
                  onChange({
                    ...type,
                    ...vali,
                    options: {
                      ...vali.options,
                      aa_pixel_exists: {
                        ...vali.options?.aa_pixel_exists,
                        enabled: newVal,
                        metadata: { aa_pixel_exists: true },
                      },
                    },
                  })
                }}
                value={vali.options?.aa_pixel_exists.enabled}
                style={{ padding: 2 }}
                color="secondary"
              />
            }
            label={t`Verify pixel exists`}
          />
        </Grid>
        <Grid item xs={8}>
          <Typography
            variant="subtitle1"
            style={{
              color: colors.blue.smokey_light,
              fontSize: '0.8125rem',
              textTransform: 'uppercase',
            }}
          >
            {t`What does this do?`}
          </Typography>
          <Typography variant="body1">
            {t`Enable this option to verify that the links submitted by users fire an Adobe Analytics Pixel. This helps ensure that URL params trigger the correct Pixels on the page.`}
          </Typography>
        </Grid>
      </Grid>
      <Divider style={{ margin: '20px 0' }} />
      <Grid container style={{ width: 'calc(100% - 35px)' }}>
        <Grid item xs={4}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              style={{ margin: 0 }}
              control={
                <Checkbox
                  checked={vali.options.aa_correct_report_suite.enabled}
                  onChange={(e, newVal) => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        aa_correct_report_suite: {
                          ...vali.options.aa_correct_report_suite,
                          enabled: newVal,
                        },
                      },
                    })
                  }}
                  value={vali.options.aa_correct_report_suite.enabled}
                  style={{ padding: 2 }}
                  color="secondary"
                />
              }
              label={t`Verify report suite`}
            />
            {vali.options.aa_correct_report_suite.enabled && getReportSuiteConfigStatus() && (
              <LinkError className={classes.error} />
            )}
          </div>
        </Grid>
        <Grid item xs={8}>
          <Typography
            variant="subtitle1"
            style={{
              color: colors.blue.smokey_light,
              fontSize: '0.8125rem',
              textTransform: 'uppercase',
            }}
          >
            {t`What does this do?`}
          </Typography>
          <Typography variant="body1">
            {t`Enabling this option will ensure that an Adobe Analytics Pixel is firing and is sending data to the correct Report Suite. In the case of multiple Pixels on a single page, Claravine will check to ensure that at least one Pixel sends data to the correct Report Suite.`}
          </Typography>
          <div>
            <RadioGroup
              name="reportSuite"
              value={rsSelection}
              onChange={(e, val) => {
                setRsSelection(val)
                onChange({
                  ...vali,
                  options: {
                    ...vali.options,
                    aa_correct_report_suite: {
                      ...vali.options.aa_correct_report_suite,
                      metadata: {},
                    },
                  },
                })
              }}
            >
              <FormControlLabel
                value="target"
                control={<Radio />}
                label={t`Inherit through a target`}
              />
              <FormControlLabel
                value="explore"
                control={<Radio />}
                label={t`Explore my report suites`}
              />
              <FormControlLabel value="input" control={<Radio />} label={t`Input by hand`} />
              {vali.options.aa_correct_report_suite.enabled && !rsSelection && (
                <span className={classes.error}>{t`*Please select an option`}</span>
              )}
            </RadioGroup>
          </div>
          <div>
            {rsSelection === 'target' && (
              <div>
                <CvsAutoComplete
                  style={{ marginBottom: 0 }}
                  label={t`Claravine target`}
                  options={Object.values(TargetContext.byId)
                    .filter(t => t.connector.connector_type_id == 1)
                    .map(t => {
                      return {
                        value: t.id,
                        label: t.name,
                      }
                    })}
                  onSelected={item => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        aa_correct_report_suite: {
                          ...vali.options.aa_correct_report_suite,
                          metadata: {
                            expected_report_suite_id: null,
                            expected_target_id: item,
                          },
                        },
                      },
                    })
                  }}
                  selected={selectedTarget}
                />
                {vali.options.aa_correct_report_suite.enabled && !selectedTarget && (
                  <span className={classes.error}>{t`*Required`}</span>
                )}
              </div>
            )}
            {rsSelection === 'explore' && (
              <div>
                <CvsAutoComplete
                  style={{ marginBottom: 0 }}
                  label={t`Adobe account`}
                  options={Object.values(byId)
                    .filter(t => t.connector_type_id == 1)
                    .map(t => {
                      return {
                        value: t.id,
                        label: t.name,
                      }
                    })}
                  onSelected={item => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        aa_correct_report_suite: {
                          ...vali.options.aa_correct_report_suite,
                          metadata: {
                            ...vali.options.aa_correct_report_suite?.metadata,
                            user_provided_account_id: item,
                          },
                        },
                      },
                    })
                    fetchRealms(item)
                  }}
                  selected={selectedAccount}
                />
                {vali.options.aa_correct_report_suite.enabled && !selectedAccount && (
                  <span className={classes.error}>{t`*Required`}</span>
                )}
                <CvsAutoComplete
                  style={{ marginBottom: 0 }}
                  label={t`Report suite`}
                  options={realms.map(t => {
                    return {
                      value: t.remote_realm_id,
                      label: t.realm_name,
                    }
                  })}
                  onSelected={item => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        aa_correct_report_suite: {
                          ...vali.options.aa_correct_report_suite,
                          metadata: {
                            ...vali.options.aa_correct_report_suite?.metadata,
                            expected_report_suite_id: item,
                            expected_target_id: null,
                          },
                        },
                      },
                    })
                    fetchReports(item)
                  }}
                  selected={selectedRealm}
                  showSpinner={selectedAccount && !realms.length}
                />
                {vali.options.aa_correct_report_suite.enabled && !selectedRealm && (
                  <span className={classes.error}>{t`*Required`}</span>
                )}
              </div>
            )}
            {rsSelection === 'input' && (
              <div>
                <TextField
                  variant="outlined"
                  label="Report Suite ID"
                  fullWidth
                  value={vali.options.aa_correct_report_suite.metadata.expected_report_suite_id}
                  onChange={e => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        aa_correct_report_suite: {
                          ...vali.options.aa_correct_report_suite,
                          metadata: {
                            expected_report_suite_id: e.target.value,
                            expected_target_id: null,
                          },
                        },
                      },
                    })
                  }}
                />
                {vali.options.aa_correct_report_suite.enabled &&
                  !vali.options.aa_correct_report_suite.metadata.expected_report_suite_id && (
                    <span style={{ color: 'red', marginTop: 5 }}>{t`*Required`}</span>
                  )}
              </div>
            )}
          </div>
        </Grid>
      </Grid>
      <Divider style={{ margin: '20px 0' }} />
      <Grid container style={{ width: 'calc(100% - 35px)' }}>
        <Grid item xs={4}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              style={{ margin: 0 }}
              control={
                <Checkbox
                  checked={vali.options.aa_correct_report.enabled}
                  onChange={(e, newVal) => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        aa_correct_report: { ...vali.options.aa_correct_report, enabled: newVal },
                      },
                    })
                  }}
                  value={vali.options.aa_correct_report.enabled}
                  style={{ padding: 2 }}
                  color="secondary"
                />
              }
              label={t`Verify report`}
            />
            {vali.options.aa_correct_report.enabled && getVerifyReportStatus() && (
              <LinkError className={classes.error} />
            )}
          </div>
        </Grid>
        <Grid item xs={8}>
          <Typography
            variant="subtitle1"
            style={{
              color: colors.blue.smokey_light,
              fontSize: '0.8125rem',
              textTransform: 'uppercase',
            }}
          >
            {t`What does this do?`}
          </Typography>
          <Typography variant="body1">
            {t`Enabling this option will ensure that an Adobe Analytics Pixel is firing that is sending data to the correct Report.`}
          </Typography>
          <Typography variant="caption">
            {t`Note: Because the Adobe Analytics Report is directly related to it's report suite, enabling this option will verify that data is being correctly sent to the unique report suite / report pair.`}
          </Typography>
          <div>
            {rsSelection === 'target' && (
              <div
                style={{
                  borderLeft: `8px solid ${colors.blue.dark}`,
                  backgroundColor: colors.gray.light,
                  padding: 8,
                  margin: '10px 0',
                }}
              >
                {t`Report suite is configured to be inherited through the provided Claravine target.`}
              </div>
            )}
            {rsSelection === 'explore' && (
              <div>
                <CvsAutoComplete
                  label={t`Report`}
                  options={reports.map(t => {
                    return {
                      value: t.id,
                      label: t.name,
                    }
                  })}
                  onSelected={item => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        aa_correct_report: {
                          ...vali.options.aa_correct_report,
                          metadata: {
                            expected_report_id: item,
                          },
                        },
                      },
                    })
                  }}
                  selected={selectedReport}
                  disabled={!selectedRealm}
                  showSpinner={selectedRealm && !reports.length}
                />
                {vali.options.aa_correct_report.enabled && !selectedReport && (
                  <span className={classes.error}>{t`*Required`}</span>
                )}
              </div>
            )}
            {rsSelection === 'input' && (
              <div>
                <TextField
                  variant="outlined"
                  label="Report ID"
                  fullWidth
                  value={vali.options.aa_correct_report.metadata.expected_report_id}
                  onChange={e => {
                    onChange({
                      ...vali,
                      options: {
                        ...vali.options,
                        aa_correct_report: {
                          ...vali.options.aa_correct_report,
                          metadata: {
                            expected_report_id: e.target.value,
                          },
                        },
                      },
                    })
                  }}
                />
                {vali.options.aa_correct_report.enabled &&
                  !vali.options.aa_correct_report.metadata.expected_report_id && (
                    <span className={classes.error}>{t`*Required`}</span>
                  )}
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    </div>
  )
}

AAValidation.propTypes = {
  validation: object,
  type: string,
  onChange: func,
}
